import { AveragePrepFqScores, PrepFqProfile } from "../../../models/users";
import React, { useCallback, useEffect, useState } from "react";
import { doc, getFirestore, updateDoc } from "firebase/firestore";

import { Button } from "@mui/material";
import CountUp from "react-countup";
import { ENHANCE_PDF_VIEWER } from "../../../constants/routes";
import GetAppIcon from "@mui/icons-material/GetApp";
import Link from "next/link";
import PrepFqScoreGraphModal from "./PrepFqScoreGraphModal";
import { PwgError } from "../../../models/common";
import Thermometer from "./Thermometer";
import clsx from "clsx";
import { firebaseApp } from "../../../firebase/firebaseClient";
import { generateEnhancePdf } from "../fetchers/generateEnhancePdf";
import { getRatingText } from "../fetchers/helpers";
import logger from "../../../utils/logger";
import { prepFqReportStyles } from "../styles/prepFqReportStyles";
import { useAuth } from "../../../firebase/useAuth";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import useUser from "../../../firebase/useUser";
import useUserMetadata from "../../../firebase/useUserMetadata";

interface Props {
  suggestionsVisible: boolean;
  animate: boolean;
  showDownloadButton: boolean;
  profile: PrepFqProfile;
  avgScores: AveragePrepFqScores;
  loading: boolean;
}

const ThermometerContents: React.FC<Props> = ({
  suggestionsVisible,
  animate,
  showDownloadButton,
  profile,
  avgScores,
  loading,
}) => {
  const { t } = useTranslation(["prepFq", "common"]);
  const router = useRouter();
  const classes = prepFqReportStyles();
  const authUser = useAuth();
  const { user } = useUser(authUser?.user?.uid);
  const { userMetadata } = useUserMetadata(authUser?.user?.uid);
  const [open, setOpen] = useState(false);
  const openModal = () => {
    setOpen(true);
  };

  const fetchPdf = useCallback(async () => {
    const firestore = getFirestore(firebaseApp);
    if (user?.user_id && userMetadata && profile) {
      try {
        if (
          !user?.documents?.enhanced_letter?.last_generated ||
          user?.documents?.enhanced_letter?.last_generated <
            profile?.date_last_updated
        ) {
          const dlUrl = await generateEnhancePdf(user.user_id);
          await updateDoc(
            doc(firestore, "prep_fq_profiles", profile.prep_fq_profile_id),
            {
              date_pdf_last_generated: new Date().getTime(),
            }
          );
          await updateDoc(doc(firestore, "users", user?.user_id), {
            [`documents.enhanced_letter`]: {
              url: dlUrl,
              last_generated: new window.Date().getTime(),
              guide: "enhance",
            },
          });
        }
      } catch (error) {
        const pwgError: PwgError = {
          user_id: authUser?.user?.uid,
          ssr: false,
          context: "fetchPdf: PdfViewer",
          date_created: new Date().getTime(),
          error: error?.toString(),
          pathname: router.asPath,
        };
        await logger.e(
          "🚀 ~ file: PdfViewer.tsx ~ line 73 ~ fetchPdf ~ error",
          error,
          pwgError
        );
      }
    }
  }, [
    user?.user_id,
    profile,
    userMetadata,
    authUser?.user?.uid,
    router.asPath,
    user?.documents?.enhanced_letter?.last_generated,
  ]);
  useEffect(() => {
    if (!loading && showDownloadButton) {
      fetchPdf();
    }
  }, [
    loading,
    showDownloadButton,
    user?.user_id,
    profile,
    userMetadata,
    fetchPdf,
  ]);

  if (loading) {
    return null;
  }

  return (
    <div className={classes.scoreContainer}>
      <Thermometer
        show={suggestionsVisible}
        animate={animate}
        profile={profile}
        avgScores={avgScores}
        loading={loading}
      />
      <div className={classes.scoresRoot}>
        <div className={clsx(classes.scoresColumn, "third-step-benchmarking")}>
          <div className={"second-step-score"} style={{ cursor: "pointer" }}>
            <p className={classes.scaledScore}>
              {suggestionsVisible ? (
                <CountUp
                  start={0}
                  end={profile?.results?.scaled_score}
                  delay={animate ? 0.5 : 0}
                  decimals={0}
                  duration={animate ? 1.5 : 0}
                />
              ) : (
                "00"
              )}
              <span className={classes.scaledScoreTotal}>/100</span>
            </p>
            <p className={classes.scoreRating}>
              {t(`${getRatingText(profile?.results?.scaled_score, avgScores)}`)}
            </p>
            <p className={classes.historyLink} onClick={openModal}>
              {t(`prepFq:report.ratings.history`)}
            </p>
          </div>
          <div className={classes.avgScoresContainer}>
            <div className={classes.avgScoresTextContainer}>
              <p className={clsx(classes.blueText, classes.avgScoresText)}>
                {t("prepFq:report.ratings.best")}
              </p>
              <p className={classes.avgScoresText}>
                {t("prepFq:report.ratings.amongPeers")}
              </p>
            </div>
            <p className={classes.avgScore}>
              {avgScores?.best_score?.toFixed(0)}
            </p>
          </div>
          <div className={classes.avgScoresContainer}>
            <div className={classes.avgScoresTextContainer}>
              <p className={clsx(classes.blueText, classes.avgScoresText)}>
                {t("prepFq:report.ratings.average")}
              </p>
              <p className={classes.avgScoresText}>
                {t("prepFq:report.ratings.amongPeers")}
              </p>
            </div>
            <p className={classes.avgScore}>
              {avgScores?.average_score?.toFixed(0)}
            </p>
          </div>
        </div>
        {showDownloadButton && (
          <div className={classes.shareContainer}>
            <Link passHref href={ENHANCE_PDF_VIEWER}>
              <Button
                className={clsx(
                  classes.downloadButton,
                  "fourth-step-download-pdf"
                )}
                variant="contained"
                color="secondary"
                sx={{ textAlign: "center" }}
              >
                <GetAppIcon className={classes.downloadIcon} />
                {t("common:buttons.prepFqReport")}
              </Button>
            </Link>
          </div>
        )}
      </div>
      <PrepFqScoreGraphModal open={open} setOpen={setOpen} />
    </div>
  );
};

export default ThermometerContents;
